/**
 * The session check, started by the HTML before any JavaScript has downloaded.
 *
 * ── WHY ──────────────────────────────────────────────────────────────────────────────────────
 *
 * Every signed-in page begins with `auth.me`: the root guard cannot let a route load until it
 * knows who is asking. Asked from the app, that request waits for the entry chunk and React to
 * download and run — most of a second on a mid-range phone on 4G — and only then starts its own
 * round trip. The question never depended on any of that code. So `index.html` asks it while the
 * page is still parsing (an inline script `vite.plugins.ts` writes), and the tRPC client picks up
 * the answer here instead of asking again. Measured on simulated 4G: DESIGN_SYSTEM.md §8.
 *
 * ── WHEN THE EARLY ANSWER MAY BE USED ────────────────────────────────────────────────────────
 *
 * ONLY FOR THE PAGE'S FIRST tRPC REQUEST, AND ONLY IF THAT REQUEST IS EXACTLY THIS ONE. Anything
 * else discards it. The answer describes the session as it was when the page loaded; the moment
 * anything else has happened — a sign-in, a switch of organisation — it may be wrong, and a
 * signed-out answer handed to the first `auth.me` after a successful login would bounce the user
 * straight back to the login page. First-or-never is the rule that makes that impossible without
 * reasoning about which requests change a session.
 *
 * A URL that does not match (the formula here and tRPC's encoding drifted apart) just falls back to
 * a normal request: slower, never wrong. `earlySession.test.ts` pins the two together.
 *
 * IMPORT-FREE, because `vite.plugins.ts` imports `EARLY_SESSION_PATH` at build time.
 */

/**
 * Where the tRPC client sends requests. Here rather than in `trpc.ts` so the build computes the
 * early request's URL with the very same function.
 */
export function trpcBaseUrl(apiUrl: string | undefined): string {
  return apiUrl ? `${apiUrl}/trpc` : '/trpc';
}

/** The request tRPC makes for `auth.me` on its own, relative to the tRPC base URL. */
export const EARLY_SESSION_PATH = '/auth.me?batch=1&input=%7B%7D';

/** What the inline script leaves on `window`. */
export interface EarlySession {
  url: string;
  response: Promise<Response>;
}

declare global {
  interface Window {
    __pmEarlySession?: EarlySession;
  }
}

/**
 * The first-or-never rule, over whatever early request there was. Returns the claim function the
 * tRPC client's `fetch` calls for every request.
 */
export function earlySessionClaim(early: EarlySession | undefined) {
  let pending = early;
  return (url: string, method: string | undefined): Promise<Response> | null => {
    const candidate = pending;
    pending = undefined;
    if (!candidate) return null;
    if ((method ?? 'GET').toUpperCase() !== 'GET' || url !== candidate.url) return null;
    return candidate.response;
  };
}

/** The claim for this page — read once, and taken off `window` so nothing else can use it. */
export const claimEarlySession = earlySessionClaim(
  typeof window === 'undefined' ? undefined : takeFromWindow(),
);

function takeFromWindow(): EarlySession | undefined {
  const early = window.__pmEarlySession;
  delete window.__pmEarlySession;
  return early;
}
